import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";

import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import MaterialEntryForm from "../../components/materialLines/MaterialEntryForm";
import { pageTitleClass } from "../../components/ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import {
  clearMaterialPurchasePartyData,
  fetchMaterialPurchaseMaterials,
  fetchMaterialPurchaseOpenOrders,
  fetchMaterialPurchaseParties,
  fetchMaterialPurchasePartyInfo,
  getMaterialPurchaseMaterials,
  getMaterialPurchaseMaterialsLoading,
  getMaterialPurchaseOpenOrders,
  getMaterialPurchaseOpenOrdersLoading,
  getMaterialPurchasePartyInfo,
  getMaterialPurchasePartyOptions,
} from "../../features/materialPurchases/materialPurchasesSlice";
import { POST } from "../../services/axios.service";
import { costPayload, linePayload } from "../../static/materialLine";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { toastStyles } from "../../static/styles";
import { BreadcrumbType } from "../../types/common";
import { IMaterialLinesFormInput } from "../../types/materialLine";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "મટીરીયલ ખરીદી", href: route.materialPurchases },
  { label: "નવી ખરીદી" },
];

const MaterialPurchaseCreatePage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const partyOptions = useAppSelector(getMaterialPurchasePartyOptions);
  const materials = useAppSelector(getMaterialPurchaseMaterials);
  const materialsLoading = useAppSelector(getMaterialPurchaseMaterialsLoading);
  const partyInfo = useAppSelector(getMaterialPurchasePartyInfo);
  const openOrders = useAppSelector(getMaterialPurchaseOpenOrders);
  const openOrdersLoading = useAppSelector(getMaterialPurchaseOpenOrdersLoading);
  const loggingUser = useAppSelector(getLoggingUser);

  // Without it the lines are saved without rates, at 0, and the purchase waits as Pending for someone who has it. The
  // additional costs are amounts too, so only a user with it enters them.
  const canRate = hasPermission(
    loggingUser?.permissions,
    permission.materialPurchasesRate
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    dispatch(fetchMaterialPurchaseParties());
    dispatch(fetchMaterialPurchaseMaterials());
    dispatch(clearMaterialPurchasePartyData());
  }, [dispatch]);

  const loadPartyData = useCallback(
    (partyId: number) => {
      if (partyId) {
        dispatch(fetchMaterialPurchasePartyInfo({ party_id: partyId }));
        dispatch(fetchMaterialPurchaseOpenOrders({ party_id: partyId }));
      } else {
        dispatch(clearMaterialPurchasePartyData());
      }
    },
    [dispatch]
  );

  const handleSubmit = async (data: IMaterialLinesFormInput) => {
    setIsSubmitting(true);
    try {
      await POST("/v1/admin/material-purchases", {
        party_id: data.party_id,
        date: moment(data.date).format("YYYY-MM-DD HH:mm:ss"),
        items: linePayload(data.items, canRate),
        ...(canRate
          ? { additional_costs: costPayload(data.additional_costs) }
          : {}),
      })
        .then((response: any) => {
          if (response.data.status === "success") {
            toast.success(response.data.message, {
              ...(toastStyles as any),
            });
            navigate(route.materialPurchases);
          } else {
            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>નવી મટીરીયલ ખરીદી ઉમેરો</h1>
      </div>

      <div className="flex flex-col gap-6">
        <MaterialEntryForm
          partyOptions={partyOptions}
          materials={materials}
          materialsLoading={materialsLoading}
          partyInfo={partyInfo}
          onPartyChange={loadPartyData}
          isPurchase
          withPrice={canRate}
          openOrders={openOrders}
          openOrdersLoading={openOrdersLoading}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          onCancel={() => navigate(route.materialPurchases)}
          submitLabel="સાચવો"
        />
      </div>
    </AppLayout>
  );
};
export default MaterialPurchaseCreatePage;
